import type { Entry } from './types';
import { useLocaleStore } from './locale';
import { common } from './catalog/common';
import { login } from './catalog/login';
import { registration } from './catalog/registration';
import { completeRegistration } from './catalog/complete-registration';
import { recover } from './catalog/recover';
import { siteSelect } from './catalog/site-select';
import { userManagement } from './catalog/user-management';
import { settings } from './catalog/settings';
import { accessRoles } from './catalog/access-roles';
import { assets } from './catalog/assets';
import { cultivation } from './catalog/cultivation';
import { erp } from './catalog/erp';
import { itsm } from './catalog/itsm';
import { soc } from './catalog/soc';
import { overview } from './catalog/overview';
import { cockpit } from './catalog/cockpit';
import { productionPlanning } from './catalog/production-planning';
import { logistics } from './catalog/logistics';
import { ecommerce } from './catalog/ecommerce';
import { auditLog } from './catalog/audit-log';
import { notifications } from './catalog/notifications';
import { microcontrollers } from './catalog/microcontrollers';
import { mcHistory } from './catalog/mc-history';
import { deviceHistory } from './catalog/device-history';
import { calibration } from './catalog/calibration';
import { siteStatus } from './catalog/siteStatus';
import { sensors } from './catalog/sensors';
import { actuators } from './catalog/actuators';
import { iotControl } from './catalog/iot-control';
import { firmware } from './catalog/firmware';
import { mapper } from './catalog/mapper';
import { masterData } from './catalog/master-data';
import { sites } from './catalog/sites';
import { servers } from './catalog/servers';
import { deviceCatalog } from './catalog/device-catalog';
import { fleetInventory } from './catalog/fleet-inventory';
import { locations } from './catalog/locations';
import { siteAssets } from './catalog/site-assets';
import { siteItsm } from './catalog/site-itsm';
import { siteChanges } from './catalog/site-changes';
import { transferOrders } from './catalog/transfer-orders';
import { receiving } from './catalog/receiving';
import { procurement } from './catalog/procurement';
import { attachments } from './catalog/attachments';
import { workOrders } from './catalog/work-orders';
import { technician } from './catalog/technician';
import { itsmCommons } from './catalog/itsm-commons';
import { problems } from './catalog/problems';
import { status } from './catalog/status';
import { ui } from './catalog/ui';
import { nav } from './catalog/nav';

export const catalog = {
  nav,
  ui,
  accessRoles,
  assets,
  auditLog,
  notifications,
  cultivation,
  erp,
  itsm,
  soc,
  overview,
  cockpit,
  productionPlanning,
  logistics,
  ecommerce,
  actuators,
  iotControl,
  firmware,
  mapper,
  masterData,
  sites,
  servers,
  deviceCatalog,
  fleetInventory,
  locations,
  siteAssets,
  siteItsm,
  siteChanges,
  transferOrders,
  receiving,
  procurement,
  attachments,
  workOrders,
  technician,
  itsmCommons,
  problems,
  status,
  microcontrollers,
  mcHistory,
  deviceHistory,
  calibration,
  siteStatus,
  sensors,
  common,
  login,
  registration,
  completeRegistration,
  recover,
  siteSelect,
  userManagement,
  settings,
} as const;

export type Catalog = typeof catalog;

type Vars = Record<string, string | number>;

/** `{name}` placeholders only — DE/EN need no ICU machinery; plurals are `_one`/`_many` key pairs. */
function interpolate(template: string, vars: Vars): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Namespace-scoped translator: `const t = useT('login'); t('welcome')`.
 * Keys are compile-time checked against the namespace; re-renders on locale change.
 */
export function useT<N extends keyof Catalog>(ns: N) {
  const locale = useLocaleStore((s) => s.locale);
  return (key: keyof Catalog[N], vars?: Vars): string => {
    // A missing key/locale must NEVER throw. Keys are compile-time checked, but a catalog↔type drift
    // (a key typed present but absent at runtime, or an entry lacking the active locale) would make
    // `(undefined)[locale]` throw *during render* — and with no error boundary that corrupts Preact's
    // reconciliation and duplicates the whole page (the "content stacks on click" bug). Degrade to the
    // other locale, then to the key itself (a visible, non-silent signal), instead of crashing render.
    const entry = catalog[ns][key] as Entry | undefined;
    const raw = entry?.[locale] ?? entry?.en ?? entry?.de ?? String(key);
    return vars ? interpolate(raw, vars) : raw;
  };
}
